import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import { X, DownloadSimple } from "@phosphor-icons/react";
import api, { API } from "@/lib/api";
import ModalPortal from "@/components/ModalPortal";

const PdfPreview = lazy(() => import("./previews/PdfPreview"));
const XlsxPreview = lazy(() => import("./previews/XlsxPreview"));
const DxfPreview = lazy(() => import("./previews/DxfPreview"));

function getExt(name) {
  return (name.split(".").pop() || "").toLowerCase();
}

function formatYmd(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return String(iso).slice(0, 10);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  } catch {
    return String(iso).slice(0, 10);
  }
}

// iOS Safari (iPhone/iPad) — vi måste detektera detta för att kringgå
// både ITP-cookies (vi använder query-token) och brist på stöd för pdfjs
// module-workers på iOS < 16.4 (vi använder då Safaris inbyggda PDF-visare).
function isIosSafari() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const isIos =
    /iPad|iPhone|iPod/.test(ua) ||
    // iPadOS 13+ rapporterar sig som Mac men har touch
    (ua.includes("Mac") && typeof document !== "undefined" && "ontouchend" in document);
  if (!isIos) return false;
  // Alla webbläsare på iOS använder WebKit → räknas som "Safari" här
  return true;
}

export default function FilePreviewModal({ object, file, onClose, onDownload }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [arrayBuf, setArrayBuf] = useState(null);
  const [tokenUrl, setTokenUrl] = useState(null); // Direktlänk med ?t=... för iframe-läge
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const ext = getExt(file.original_name);
  const ios = useMemo(() => isIosSafari(), []);

  useEffect(() => {
    let cancelled = false;
    let createdUrl = null;

    (async () => {
      try {
        setLoading(true);
        setError(null);

        // 1) Hämta en kortlivad preview-token (kringgår ITP-cookies i iOS Safari)
        const tokResp = await api.post(
          `/objects/${object.object_id}/files/${file.file_id}/preview-token`,
          {}
        );
        const token = tokResp.data?.token;
        if (!token) throw new Error("Saknad preview-token");

        const directUrl = `${API}/objects/${object.object_id}/files/${file.file_id}/download?t=${encodeURIComponent(token)}&inline=1`;
        if (cancelled) return;
        setTokenUrl(directUrl);

        // 2) iOS Safari + PDF → använd inbyggd PDF-visare via iframe.
        // Vi behöver inte hämta blob:en själva, iframen laddar URL:en direkt.
        if (ios && ext === "pdf") {
          setLoading(false);
          return;
        }

        // 3) Övriga fall: hämta filen som ArrayBuffer (utan cookies — vi har redan token i URL:en)
        const resp = await fetch(directUrl, { credentials: "omit" });
        if (!resp.ok) {
          let detail = "";
          try {
            const body = await resp.json();
            detail = body?.detail || "";
          } catch {
            // body är inte JSON — ignorera
          }
          throw Object.assign(new Error(detail || resp.statusText), { status: resp.status });
        }
        const buf = await resp.arrayBuffer();
        if (cancelled) return;
        setArrayBuf(buf);

        const mime = resp.headers.get("content-type") || "application/octet-stream";
        const blob = new Blob([buf], { type: mime });
        createdUrl = URL.createObjectURL(blob);
        setBlobUrl(createdUrl);
      } catch (e) {
        const status = e?.status || e?.response?.status;
        const apiDetail = e?.response?.data?.detail || e?.message;
        const msg = status
          ? `Kunde inte hämta filen för förhandsvisning (HTTP ${status}${apiDetail ? ` – ${apiDetail}` : ""})`
          : `Kunde inte hämta filen för förhandsvisning${apiDetail ? ` – ${apiDetail}` : ""}`;
        if (!cancelled) setError(msg);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [object.object_id, file.file_id, ext, ios]);

  const renderViewer = () => {
    if (loading) {
      return (
        <div className="flex-1 flex items-center justify-center text-text-muted text-sm" data-testid="preview-loading">
          Laddar förhandsvisning…
        </div>
      );
    }
    if (error) {
      return (
        <div className="flex-1 flex flex-col items-center justify-center text-sm text-[var(--status-danger)] gap-3 p-4 text-center" data-testid="preview-error">
          <div>{error}</div>
          <button
            onClick={() => onDownload && onDownload(file)}
            className="tk-btn-outline !py-1.5 !px-3 inline-flex items-center gap-1.5 text-xs"
            data-testid="preview-error-download-btn"
          >
            <DownloadSimple size={14} />
            <span>Ladda ner istället</span>
          </button>
        </div>
      );
    }

    if (ext === "pdf") {
      // iOS Safari: använd inbyggd PDF-rendering (QuickLook) via iframe.
      // Detta fungerar oavsett iOS-version och slipper pdfjs module-worker.
      if (ios && tokenUrl) {
        return (
          <iframe
            src={tokenUrl}
            title={file.original_name}
            className="flex-1 w-full bg-white"
            data-testid="pdf-preview-iframe"
          />
        );
      }
      return (
        <Suspense fallback={<div className="flex-1 flex items-center justify-center text-text-muted text-sm">Laddar PDF-läsare…</div>}>
          <PdfPreview blobUrl={blobUrl} />
        </Suspense>
      );
    }
    if (ext === "xls" || ext === "xlsx") {
      return (
        <Suspense fallback={<div className="flex-1 flex items-center justify-center text-text-muted text-sm">Laddar Excel-läsare…</div>}>
          <XlsxPreview arrayBuf={arrayBuf} />
        </Suspense>
      );
    }
    if (ext === "dxf") {
      return (
        <Suspense fallback={<div className="flex-1 flex items-center justify-center text-text-muted text-sm">Laddar DXF-läsare…</div>}>
          <DxfPreview blobUrl={blobUrl} />
        </Suspense>
      );
    }
    if (["jpg", "jpeg", "png", "webp", "gif", "heic", "heif"].includes(ext)) {
      return (
        <div className="flex-1 flex items-center justify-center bg-black/50 overflow-auto p-2">
          <img
            src={blobUrl}
            alt={file.original_name}
            className="max-w-full max-h-full object-contain"
            data-testid="image-preview"
          />
        </div>
      );
    }
    if (["mp4", "mov", "webm", "m4v", "3gp"].includes(ext)) {
      return (
        <div className="flex-1 flex items-center justify-center bg-black">
          <video
            src={blobUrl}
            controls
            playsInline
            className="max-w-full max-h-full"
            data-testid="video-preview"
          />
        </div>
      );
    }
    return (
      <div className="flex-1 flex items-center justify-center text-text-muted text-sm">
        Förhandsvisning stöds inte för detta filformat.
      </div>
    );
  };

  return (
    <ModalPortal>
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-2 sm:p-4 bg-black/70"
      onClick={onClose}
      data-testid="preview-backdrop"
    >
      <div
        className="bg-surface rounded-2xl w-full max-w-5xl h-[90vh] flex flex-col border border-app overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="file-preview-modal"
      >
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-app">
          <div className="flex-1 min-w-0">
            <h2
              className="font-heading text-base sm:text-lg truncate"
              title={file.original_name}
              data-testid="preview-filename"
            >
              {file.original_name}
            </h2>
            {file.updated_by_integration && file.updated_at && (
              <div
                className="text-[11px] text-text-muted mt-0.5 truncate"
                data-testid="preview-integration-stamp"
                title={`Senast ersatt via ${file.updated_by_integration}`}
              >
                Uppdaterad: {formatYmd(file.updated_at)} via {file.updated_by_integration}
              </div>
            )}
          </div>
          <button
            onClick={() => onDownload && onDownload(file)}
            className="tk-btn-outline !py-1.5 !px-3 inline-flex items-center gap-1.5 text-xs"
            title="Ladda ner"
            data-testid="preview-download-btn"
          >
            <DownloadSimple size={14} />
            <span className="hidden sm:inline">Ladda ner</span>
          </button>
          <button
            onClick={onClose}
            className="text-text-muted hover:text-text-primary p-1"
            data-testid="preview-close-btn"
            aria-label="Stäng"
          >
            <X size={20} />
          </button>
        </div>
        <div className="flex-1 flex min-h-0 bg-black/5">
          {renderViewer()}
        </div>
      </div>
    </div>
    </ModalPortal>
  );
}
